/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  VStack,
  Tooltip,
  Badge
} from '@chakra-ui/react'
import { format } from 'date-fns'
import Card from '@/components/cards/base/Card'
import { formatPrice } from '@/components/license-history/license-history-summary-modal/handlers'
import RevenueValue from '@/components/revenue-breakdown/revenue-table/RevenueValue'
import EmptyValue from '@/components/revenue-breakdown/revenue-table/EmptyValue'

export default function SamActivityTable ({ activities }) {
  if (!activities || activities.length === 0) {
    return (
      <Card>
        <div className="text-center text-gray-500 py-8">
          No activity yet
        </div>
      </Card>
    )
  }

  const getEventTypeBadge = (type) => {
    const colors = {
      BUILD_ACQUIRED: 'purple',
      AI_METADATA_GENERATED: 'blue',
      SAM_CREATED: 'green',
      AI_USAGE_LISTED: 'orange'
    }
    return colors[type] || 'gray'
  }

  const getStatusBadge = (status) => {
    const colors = {
      ACQUIRED: 'green',
      GENERATED: 'blue',
      CREATED: 'green',
      LISTED: 'orange',
      PENDING: 'yellow',
      FAILED: 'red'
    }
    return colors[status] || 'gray'
  }

  return (
    <VStack spacing={4} align="stretch">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">SAM Activity Timeline</h3>
        <Text className="text-sm text-gray-600">
          Showing {activities.length} event{activities.length !== 1 ? 's' : ''}
        </Text>
      </div>

      <div className="overflow-x-auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Timestamp</Th>
              <Th>Event Type</Th>
              <Th>Actor</Th>
              <Th isNumeric>Price</Th>
              <Th isNumeric>My Revenue</Th>
              <Th>Status</Th>
              <Th>Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {activities.map((activity, index) => (
              <Tr key={index}>
                <Td>
                  <Text className="text-sm whitespace-nowrap">
                    {format(new Date(activity.timestamp), 'MMM dd, yyyy')}
                  </Text>
                  <Text className="text-sm text-gray-500">
                    {format(new Date(activity.timestamp), 'HH:mm')}
                  </Text>
                </Td>
                <Td>
                  <Badge
                    colorScheme={getEventTypeBadge(activity.type)}
                    variant="subtle"
                  >
                    {activity.eventType}
                  </Badge>
                </Td>

                <Td>
                  {activity.actor
                    ? <VStack spacing={0} align="start">
                        <Text className="text-sm font-medium">
                          {activity.actor.nickname || `User ${activity.actor.id}`}
                        </Text>
                        <Text className="text-gray-500">
                          {activity.actorRole}
                        </Text>
                        {activity.actor.zetoniumId && (
                          <Tooltip label={activity.actor.zetoniumId}>
                            <Text className="text-gray-500 truncate max-w-[120px]">
                              {activity.actor.zetoniumId}
                            </Text>
                          </Tooltip>
                        )}
                      </VStack>
                    : <VStack spacing={0} align="start">
                        <Text className="text-sm font-medium">Me</Text>
                        <Text className="text-gray-500">
                          {activity.actorRole}
                        </Text>
                      </VStack>
                  }
                </Td>
                <Td isNumeric>
                  {activity.price !== null
                    ? <RevenueValue value={activity.price} />
                    : <EmptyValue value="—" />
                  }
                </Td>
                <Td isNumeric>
                  { activity.myRevenue !== null
                    ? <RevenueValue value={activity.myRevenue} />
                    : <EmptyValue value="—" />
                  }
                </Td>
                <Td>
                  {activity.status && (
                    <Badge
                      colorScheme={getStatusBadge(activity.status)}
                      variant="solid"
                    >
                      {activity.status}
                    </Badge>
                  )}
                </Td>
                <Td>
                  <VStack spacing={0} align="start">
                    {activity.type === 'BUILD_ACQUIRED' && (
                      <>
                        <Text className="text-sm text-gray-600">
                          Asset: {activity.data.asset?.title}
                        </Text>
                        <Text className="text-sm text-gray-600">
                          Royalty: {formatPrice(activity.data.requiredRoyalty)}%
                        </Text>
                      </>
                    )}

                    {activity.type === 'AI_METADATA_GENERATED' && (
                      <>
                        <Text className="text-sm text-gray-600">
                          {activity.count} metadata file{activity.count !== 1 ? 's' : ''} generated
                        </Text>
                        {activity.data[0]?.embeddingModel && (
                          <Text className="text-sm text-gray-600">
                            Model: {activity.data[0].embeddingModel.name}
                          </Text>
                        )}
                      </>
                    )}
                    {activity.type === 'SAM_CREATED' && (
                      <>
                        <Text className="text-sm text-gray-600">
                          SAM ID: {activity.samId}
                        </Text>
                      </>
                    )}
                    {activity.type === 'AI_USAGE_LISTED' && (
                      <>
                        <Text className="text-sm text-gray-600">
                          {activity.data.numberOfLicenses} licenses available
                        </Text>
                        <Text className="text-sm text-gray-600">
                          Term: {activity.data.licenseTerm} days
                        </Text>
                      </>
                    )}
                  </VStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </div>
    </VStack>
  )
}
